import { useCallback, useEffect, useState } from 'react';
import { Panel, useReactFlow, useStore } from '@xyflow/react';
import { Maximize2, Minus, Plus } from 'lucide-react';
import {
  DIAGRAM_OPEN_ZOOM_OPTIONS,
  normalizeDiagramOpenZoom,
  type DiagramOpenZoom,
} from '@ukrop-nm/shared';
import { SETTINGS_CHANGED_EVENT } from '../auth/accountScope';
import { loadUserUiSettings, saveUserUiSettings } from '../auth/userSettings';
import { useDiagram } from '../hooks/useDiagram';
import { applyDiagramViewport } from './diagramViewport';

export function DiagramZoomControls() {
  const diagram = useDiagram();
  const { zoomIn, zoomOut, fitView, setCenter } = useReactFlow();
  const zoom = useStore((s) => s.transform[2]);
  const [openZoom, setOpenZoom] = useState<DiagramOpenZoom>(() => loadUserUiSettings().diagramOpenZoom);

  useEffect(() => {
    const onSettings = () => setOpenZoom(loadUserUiSettings().diagramOpenZoom);
    window.addEventListener(SETTINGS_CHANGED_EVENT, onSettings);
    return () => window.removeEventListener(SETTINGS_CHANGED_EVENT, onSettings);
  }, []);

  const applyOpen = useCallback(
    (value: DiagramOpenZoom) => {
      applyDiagramViewport({ fitView, setCenter }, value, diagram.nodes);
    },
    [fitView, setCenter, diagram.nodes],
  );

  const onOpenZoomChange = (raw: string) => {
    const value = normalizeDiagramOpenZoom(raw === 'fit' ? 'fit' : Number(raw));
    setOpenZoom(value);
    saveUserUiSettings({ diagramOpenZoom: value });
    applyOpen(value);
  };

  const fitNow = () => {
    void fitView({
      padding: 0.06,
      duration: 200,
      includeHiddenNodes: true,
      minZoom: 0.02,
      maxZoom: 2,
    });
  };

  const currentPct = Math.round(zoom * 100);

  return (
    <Panel position="bottom-left" className="diagram-zoom-controls">
      <button type="button" className="diagram-zoom-btn" title="Зменшити" onClick={() => zoomOut({ duration: 150 })}>
        <Minus size={16} />
      </button>
      <button type="button" className="diagram-zoom-btn" title="Збільшити" onClick={() => zoomIn({ duration: 150 })}>
        <Plus size={16} />
      </button>
      <button type="button" className="diagram-zoom-btn" title="Вмістити всю схему" onClick={fitNow}>
        <Maximize2 size={16} />
      </button>
      <span className="diagram-zoom-current" title="Поточний масштаб">
        {currentPct}%
      </span>
      <label className="diagram-zoom-open">
        <span className="sr-only">Масштаб при відкритті</span>
        <select
          value={String(openZoom)}
          title="Масштаб при відкритті схеми"
          onChange={(e) => onOpenZoomChange(e.target.value)}
        >
          {DIAGRAM_OPEN_ZOOM_OPTIONS.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    </Panel>
  );
}
